import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Eye, FileText, Users, MousePointerClick } from "lucide-react";

interface DashboardStatsProps {
  startDate?: Date | null;
  endDate?: Date | null;
}

export default function DashboardStats({ startDate, endDate }: DashboardStatsProps) {
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalViews: 0,
    totalSubscribers: 0,
    totalClicks: 0,
    avgViewsPerPost: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [startDate, endDate]);

  const loadStats = async () => {
    try {
      // Build posts query with date filters
      let postsQuery = supabase
        .from("posts")
        .select("views")
        .eq("status", "published");

      if (startDate) {
        postsQuery = postsQuery.gte("created_at", startDate.toISOString());
      }
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        postsQuery = postsQuery.lte("created_at", endOfDay.toISOString());
      }

      const { data: posts, error: postsError } = await postsQuery;
      if (postsError) throw postsError;

      const totalPosts = posts?.length || 0;
      const totalViews = posts?.reduce((sum, post) => sum + (post.views || 0), 0) || 0;

      // Get subscribers with date filter
      let subsQuery = supabase
        .from("newsletter_subscribers")
        .select("*", { count: "exact", head: true })
        .eq("status", "active");

      if (startDate) {
        subsQuery = subsQuery.gte("subscribed_at", startDate.toISOString());
      }
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        subsQuery = subsQuery.lte("subscribed_at", endOfDay.toISOString());
      }

      const { count: subscriberCount, error: subsError } = await subsQuery;
      if (subsError) throw subsError;

      // Get clicks with date filter
      let clicksQuery = supabase
        .from("kopfai_clicks")
        .select("*", { count: "exact", head: true });

      if (startDate) {
        clicksQuery = clicksQuery.gte("clicked_at", startDate.toISOString());
      }
      if (endDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        clicksQuery = clicksQuery.lte("clicked_at", endOfDay.toISOString());
      }

      const { count: clicksCount, error: clicksError } = await clicksQuery;
      if (clicksError) throw clicksError;

      setStats({
        totalPosts,
        totalViews,
        totalSubscribers: subscriberCount || 0,
        totalClicks: clicksCount || 0,
        avgViewsPerPost: totalPosts > 0 ? Math.round(totalViews / totalPosts) : 0,
      });
    } catch (error) {
      console.error("Error loading stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: "Total Posts",
      value: stats.totalPosts,
      icon: FileText,
      description: "Published articles",
    },
    {
      title: "Total Views",
      value: stats.totalViews.toLocaleString(),
      icon: Eye,
      description: "All-time page views",
    },
    {
      title: "Clicks KopfAI",
      value: stats.totalClicks,
      icon: MousePointerClick,
      description: "CTA button clicks",
    },
    {
      title: "Newsletter Subscribers",
      value: stats.totalSubscribers,
      icon: Users,
      description: "Active subscribers",
    },
    {
      title: "Avg Views/Post",
      value: stats.avgViewsPerPost,
      icon: BarChart,
      description: "Average engagement",
    },
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className={i === 0 ? "col-span-2 sm:col-span-1" : ""}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
              <div className="h-4 w-16 sm:w-24 bg-muted animate-pulse rounded" />
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
              <div className="h-6 sm:h-8 w-12 sm:w-16 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Card key={stat.title} className={index === 0 ? "col-span-2 sm:col-span-1" : ""}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 p-3 sm:p-6 sm:pb-2">
              <CardTitle className="text-xs sm:text-sm font-medium">{stat.title}</CardTitle>
              <Icon className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
              <div className="text-xl sm:text-2xl font-bold">{stat.value}</div>
              <p className="text-[10px] sm:text-xs text-muted-foreground hidden sm:block">{stat.description}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
